import { useEffect, useRef } from "react";

/**
 * While `open` is true, push a marker entry onto `window.history` so that
 * pressing the system / browser Back button pops only the dialog rather
 * than navigating the underlying page.
 *
 * Behaviour:
 *  - When `open` flips true (and `enabled` is not false), we push a
 *    `{ __dialog: id }` state with the current URL so the visible URL
 *    doesn't change.
 *  - A `popstate` listener watches for our marker being popped. If the
 *    next state no longer carries our id, we call `onClose()`.
 *  - When the dialog closes through any other path (X, Escape, backdrop,
 *    Cancel, programmatic) while our marker is still on top, we call
 *    `history.back()` once to remove our entry so the next Back press
 *    behaves normally. The `back()` is deferred to a microtask so that
 *    a sibling dialog opening in the SAME tick ("close A then open B
 *    in the same handler") has a chance to absorb our marker via its
 *    own pushState — that avoids a stray popstate from our pending
 *    back() closing the sibling one frame after it opens.
 *  - Nested dialogs work because each instance owns its own per-mount
 *    id and only reacts to its own marker. We only absorb sibling
 *    markers that are PENDING cleanup, never markers belonging to a
 *    still-open dialog.
 *  - Programmatic navigation while open is handled by monkey-patching
 *    `history.pushState` / `history.replaceState` for the duration any
 *    marker is active: if the navigation would push on top of our
 *    marker, we replace instead, "absorbing" the marker into the new
 *    entry. That way the user's history stays clean — no orphan
 *    entry remains after the dialog tears down.
 *  - No-op during SSR and resilient to React 18 StrictMode double-
 *    mounts via a `didPush` ref guard.
 */

type AbsorbCallback = () => void;
const activeMarkers = new Map<string, AbsorbCallback>();
// Markers whose owning dialog has unmounted/closed and whose cleanup
// `history.back()` has been deferred to a microtask. A sibling dialog
// opening in the same tick can absorb one of these via `replaceState`
// so its entry takes the pending marker's slot.
const pendingMarkers = new Set<string>();
let origPushState:
  | ((data: unknown, unused: string, url?: string | null) => void)
  | null = null;
let origReplaceState:
  | ((data: unknown, unused: string, url?: string | null) => void)
  | null = null;

function ensurePatched() {
  if (typeof window === "undefined") return;
  if (origPushState) return;
  origPushState = window.history.pushState.bind(window.history);
  origReplaceState = window.history.replaceState.bind(window.history);

  const absorbIfMarkerOnTop = () => {
    const current = window.history.state as { __dialog?: string } | null;
    const markerId = current?.__dialog;
    if (markerId && activeMarkers.has(markerId)) {
      const cb = activeMarkers.get(markerId)!;
      activeMarkers.delete(markerId);
      pendingMarkers.delete(markerId);
      cb();
      return true;
    }
    return false;
  };

  window.history.pushState = function (
    data: unknown,
    unused: string,
    url?: string | null,
  ) {
    if (absorbIfMarkerOnTop()) {
      // Replace into the marker slot so the app's new entry takes its
      // place — total history length stays where it would have been
      // without the dialog ever being opened.
      return origReplaceState!(data, unused, url ?? null);
    }
    return origPushState!(data, unused, url ?? null);
  };
  window.history.replaceState = function (
    data: unknown,
    unused: string,
    url?: string | null,
  ) {
    absorbIfMarkerOnTop();
    return origReplaceState!(data, unused, url ?? null);
  };
}

function tryUnpatch() {
  if (activeMarkers.size > 0) return;
  if (!origPushState || !origReplaceState) return;
  window.history.pushState = origPushState;
  window.history.replaceState = origReplaceState;
  origPushState = null;
  origReplaceState = null;
}

export function useBackButtonClose(
  open: boolean,
  onClose: () => void,
  opts?: { enabled?: boolean },
): void {
  const enabled = opts?.enabled !== false;
  const idRef = useRef<string | null>(null);
  const didPushRef = useRef(false);
  const absorbedRef = useRef(false);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!enabled) return;
    if (!open) return;

    const id =
      (typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2)) +
      ":" +
      Date.now().toString(36);
    idRef.current = id;
    absorbedRef.current = false;

    // Guard against StrictMode double-invocation: only push once per id.
    if (!didPushRef.current) {
      didPushRef.current = true;
      try {
        ensurePatched();
        const existing =
          (window.history.state && typeof window.history.state === "object"
            ? (window.history.state as Record<string, unknown>)
            : null) ?? null;
        // Register absorption callback BEFORE pushing so a pushState that
        // synchronously fires during patching can still be intercepted.
        activeMarkers.set(id, () => {
          absorbedRef.current = true;
          didPushRef.current = false;
          idRef.current = null;
        });

        // If a sibling dialog's marker is on top of the history stack
        // AND its cleanup has already deferred its own `history.back()`
        // (i.e. it's in `pendingMarkers`), absorb its slot via
        // `replaceState` instead of stacking on top. This is the
        // "close A then open B in the same handler" handoff: we take
        // over A's history slot so A's pending back() — when it fires
        // and finds its marker already gone — won't pop us. Without
        // this, the stray popstate from A's pending back would close
        // our dialog one frame after we opened.
        const currentState =
          (window.history.state && typeof window.history.state === "object"
            ? (window.history.state as Record<string, unknown>)
            : null) ?? null;
        const topId =
          typeof currentState?.__dialog === "string"
            ? (currentState.__dialog as string)
            : undefined;
        const pendingSibling =
          topId && pendingMarkers.has(topId) && activeMarkers.has(topId)
            ? topId
            : undefined;

        if (pendingSibling) {
          const sibCb = activeMarkers.get(pendingSibling)!;
          activeMarkers.delete(pendingSibling);
          pendingMarkers.delete(pendingSibling);
          // Notify the sibling so its deferred microtask skips back().
          sibCb();
          // Use the unpatched replaceState — we've already manually
          // handled the absorption above and don't want to recurse.
          origReplaceState!(
            { ...(existing ?? {}), __dialog: id },
            "",
            window.location.href,
          );
        } else {
          // Use the bound original to bypass our own patched wrapper —
          // we don't want to absorb a marker belonging to a sibling
          // dialog that is still open (only pending ones above).
          origPushState!(
            { ...(existing ?? {}), __dialog: id },
            "",
            window.location.href,
          );
        }
      } catch {
        // pushState can throw in exotic sandboxes; degrade gracefully.
        activeMarkers.delete(id);
        pendingMarkers.delete(id);
        didPushRef.current = false;
        idRef.current = null;
        tryUnpatch();
        return;
      }
    }

    const handler = (_e: PopStateEvent) => {
      const state = window.history.state as { __dialog?: string } | null;
      const stillOurs =
        state && typeof state === "object" && state.__dialog === idRef.current;
      if (stillOurs) return;
      // Our entry was popped — close the dialog.
      const myId = idRef.current;
      didPushRef.current = false;
      idRef.current = null;
      if (myId) {
        activeMarkers.delete(myId);
        pendingMarkers.delete(myId);
      }
      try {
        onCloseRef.current();
      } finally {
        tryUnpatch();
      }
    };

    window.addEventListener("popstate", handler);

    return () => {
      window.removeEventListener("popstate", handler);
      const myId = idRef.current;
      // If the marker was absorbed by a programmatic navigation (or by
      // a sibling dialog opening in the same tick), the history is
      // already clean — nothing to undo.
      if (absorbedRef.current) {
        absorbedRef.current = false;
        didPushRef.current = false;
        idRef.current = null;
        if (myId) {
          activeMarkers.delete(myId);
          pendingMarkers.delete(myId);
        }
        tryUnpatch();
        return;
      }
      // If the dialog was closed via something other than Back (X / Esc /
      // backdrop / Cancel / programmatic close), and our marker is still
      // on top of the history stack, pop it so we don't leave an orphan
      // entry — but defer the pop to a microtask so a chained sibling
      // dialog opening in the same tick can absorb our marker first.
      // We keep ourselves in `activeMarkers` AND register in
      // `pendingMarkers` across the microtask boundary so the sibling's
      // mount can identify and claim our slot.
      if (didPushRef.current && myId) {
        const state = window.history.state as { __dialog?: string } | null;
        const stillOurs =
          state && typeof state === "object" && state.__dialog === myId;
        if (stillOurs) {
          didPushRef.current = false;
          idRef.current = null;
          pendingMarkers.add(myId);
          queueMicrotask(() => {
            pendingMarkers.delete(myId);
            if (absorbedRef.current) {
              absorbedRef.current = false;
              activeMarkers.delete(myId);
              tryUnpatch();
              return;
            }
            activeMarkers.delete(myId);
            const cur = window.history.state as
              | { __dialog?: string }
              | null;
            const stillOnTop =
              cur && typeof cur === "object" && cur.__dialog === myId;
            if (stillOnTop) {
              try {
                window.history.back();
              } catch {
                /* ignore */
              }
            }
            tryUnpatch();
          });
          return;
        }
        // Marker is buried under other entries but wasn't absorbed —
        // best we can do is forget about it; the orphan would be a
        // single repeat-URL Back press in the worst case.
        activeMarkers.delete(myId);
        pendingMarkers.delete(myId);
      }
      didPushRef.current = false;
      idRef.current = null;
      tryUnpatch();
    };
  }, [open, enabled]);
}
